import React, { useRef } from 'react';
import { View, Text, TouchableOpacity, Animated } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { Popup, ImageUpload, isWeb, isPhone, LEVELS, navigateTo, Alert } from '@helpers';
import { checkEvent } from '@reducers/utils';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import ColorWrapper from '@styles/colors';
import { width, scale, bottomBarHeight } from '@components/frame';

import TouchableElement from '@components/common/touchableElement';
import InviteUserModal from '@components/settings/v2/structure/users/users/inviteUser';
import AddMachineModal from '@components/settings/v2/structure/machines/machines/addMachine';
import Part from './modal/Part';
import FeatureFlags from '@components/featureFlags';
import AddTool from '@components/tool/components/addTool';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { getBoxShadowStyle } from '@styles/shadow';
import {
  IMAGE_UPLOAD_FOOTER_TEXT,
  IMAGE_UPLOAD_HEADER_TEXT,
  IMAGE_UPLOAD_MAIN_TEXT,
  modalBg,
  SCREEN_BORDER_RADIUS,
} from '../v2/pages/common/constants/base.constants';

const AddMenu = ({ C, triggerAddMenu, globalSelectedLevel, globalImageContext, isWithERP }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const NavigationButton = ({ icon, name, action, style }) => {
    return (
      <TouchableElement
        onPress={() => {
          triggerAddMenu();
          action();
        }}
      >
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              boxStyle,
              {
                // width: scale(124),
                flex: 1,
                height: scale(113),
                backgroundColor: C(pressHoverColor('Button/Light/Secondary')),
                borderRadius: scale(12),
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
              },
              style,
            ]}
          >
            <View style={{ marginBottom: scale(9) }}>
              <Svg icon="plusSmall" width={scale(18)} height={scale(18)} color={'Button/Light/Primary'} />
            </View>
            <View style={{ marginBottom: scale(9) }}>
              <Text style={[font['Button 1'], { textAlign: 'center', color: C('Button/Light/Secondary Text') }]}>{i18n.t(name)}</Text>
            </View>
            <View>
              <Svg icon={icon.name} width={scale(icon.width)} height={scale(icon.height)} color={'Button/Light/Primary'} />
            </View>
          </View>
        )}
      </TouchableElement>
    );
  };

  const LineButton = ({ action, name, backgroundColor = 'Button/Light/Secondary', color = 'Button/Light/Secondary Text' }) => {
    return (
      <TouchableElement
        onPress={() => {
          triggerAddMenu();
          action();
        }}
      >
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              boxStyle,
              {
                backgroundColor: C(pressHoverColor(backgroundColor)),
                borderRadius: scale(12),
                flexDirection: 'row',
                marginTop: scale(18),
                height: scale(46),
                alignItems: 'center',
                paddingLeft: scale(21),
              },
            ]}
          >
            <View>
              <Svg icon="plusSmall" width={scale(18)} height={scale(18)} color={color} />
            </View>
            <View style={{ marginLeft: scale(23) }}>
              <Text style={[font['Button 1'], { color: C(color) }]}>{i18n.t(name)}</Text>
            </View>
          </View>
        )}
      </TouchableElement>
    );
  };
  const toggleInviteUser = () => {
    Popup({
      Component: InviteUserModal,
    });
  };

  const toggleAddMachine = () => {
    Popup({
      Component: AddMachineModal,
      width: 580,
    });
  };

  const toggleAddPart = () => {
    Popup({
      Component: Part,
      width: 580,
    });
  };

  const isPartLevels = [LEVELS.Parts, 'part'].includes(globalSelectedLevel) && !isWithERP;
  const isNcFileLevels = [LEVELS.NCFiles, 'ncFile'].includes(globalSelectedLevel);
  const isToolsLevels = [LEVELS.Tools, 'tools'].includes(globalSelectedLevel);

  const toggleImageUpload = () => {
    ImageUpload({
      //  think here //
      withoutAddExisting: true,
      uploadStart: () => {},
      uploadEnd: () => {},
      saveToMainStore: Boolean(globalImageContext),
      tags: globalImageContext || [],
    });
  };

  const toggleNCFileUpload = () => {
    ImageUpload({
      addingNcFile: true,
      noPhoto: true,
      uploadStart: (data) => {
        const fileName = data?.name;
        if (!fileName) return;

        Alert({
          title: i18n.t('Processing NC file...'),
          loadingIndicator: true,
          actions: [{ text: i18n.t('Dismiss'), style: 'secondary' }],
        });

        checkEvent('OnProcessedNcFileDocument', {
          timeout: 30000,
          connectionName: 'appHub',
          matcher: (eventData) => eventData?.ncFile?.ncFileName === fileName,
        })
          .then((eventData) => {
            const ncFileId = eventData?.ncFile?.ncFileId;
            const ncFileName = eventData?.ncFile?.ncFileName;
            if (!ncFileId) return;

            Alert({
              title: i18n.t('NC File uploaded'),
              text: i18n.t('{{fileName}} has been processed successfully', { fileName: ncFileName }),
              actions: [
                {
                  text: i18n.t('Open'),
                  style: 'primary',
                  onPress: () =>
                    navigateToMainDetailsScreen({
                      mainScreen: NCFileDetailsScreenName,
                      subScreen: NCFileDetailsScreen.NC_FILE,
                      params: { perspective: Perspective.NC_FILE, ncFileId, ncFileName },
                    }),
                },
                { text: i18n.t('Dismiss'), style: 'secondary' },
              ],
            });
          })
          .catch(() => {
            Alert({ title: i18n.t('NC file processing timed out'), type: 'danger' });
          });
      },
      uploadEnd: () => {},
      documentType: 'NcFile',
      tags: [],
      headerText: IMAGE_UPLOAD_HEADER_TEXT.AddNcFile,
      footerText: IMAGE_UPLOAD_FOOTER_TEXT.AddNcFile,
      mainText: IMAGE_UPLOAD_MAIN_TEXT.NcFile,
      addExistingSearchLevel: LEVELS.NCFiles,
    });
  };

  const toggleAddTool = () => {
    Popup({
      Component: (p) => (
        <AddTool
          {...{
            ...p,
            showAddTool: true,
            toogleShowAddTool: p.hide,
            rounded: !isPhone,
          }}
        />
      ),
    });
  };

  const renderButtons = [
    {
      action: toggleImageUpload,
      name: 'Image or Document',
      isMain: !(isPartLevels || isNcFileLevels || isToolsLevels) || globalSelectedLevel == LEVELS.ImagesDocuments,
    },
    { action: toggleNCFileUpload, name: 'NC File', isMain: isNcFileLevels },
    { action: toggleAddTool, name: 'Tool', isMain: isToolsLevels },
  ];

  if (!isWithERP) {
    renderButtons.push({ action: toggleAddPart, name: 'Part', isMain: isPartLevels });
  }

  return (
    <TouchableOpacity
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: C('Overlay/Light/Dim strong'),
        position: 'absolute',
        zIndex: 1000000000,
      }}
      onPress={() => triggerAddMenu()}
      activeOpacity={1}
    >
      <View
        style={[
          {
            width: '100%',
            height: '100%',
            alignItems: 'center',
          },
          !isWeb && {
            justifyContent: 'flex-end',
          },
        ]}
      >
        <View
          style={[
            {
              width: '100%',
              paddingRight: scale(isWeb ? 18 : 0),
              flexDirection: 'row',
              justifyContent: isWeb ? 'flex-end' : 'center',
              paddingBottom: scale(isWeb || isPhone ? 0 : bottomBarHeight + 9),
            },
          ]}
        >
          <View
            style={[
              getBoxShadowStyle({ type: 'Narrow', C }),
              {
                backgroundColor: C(modalBg),
                width: isPhone ? width : scale(444),
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                borderWidth: 1,
                borderColor: C('Hairline/Light/Primary'),
                marginTop: scale(4),
                alignItems: 'center',
              },
            ]}
          >
            <View
              style={[
                {
                  padding: scale(18),
                  width: '100%',
                },
                isPhone && {
                  paddingBottom: scale(bottomBarHeight),
                },
              ]}
            >
              <View style={{ flexDirection: 'row', justifyContent: 'space-around' }}>
                <NavigationButton {...{ name: 'User', icon: { name: 'user', width: 25, height: 27 }, action: toggleInviteUser }} />
                <NavigationButton
                  {...{
                    name: 'Device',
                    icon: { name: 'phone', width: 17, height: 27 },
                    action: () => navigateTo('AddDevice'),
                    //
                    style: { marginHorizontal: scale(18) },
                  }}
                />
                <NavigationButton {...{ name: 'Machine', icon: { name: 'machine', width: 36, height: 27 }, action: toggleAddMachine }} />
              </View>
              <FeatureFlags {...{ name: 'search-image-documents' }}>
                {renderButtons.map((i) =>
                  i.isMain ? (
                    <LineButton
                      key={i.name}
                      {...{
                        ...i,
                        backgroundColor: 'Button/Light/Primary',
                        color: 'Button/Light/Primary Text',
                      }}
                    />
                  ) : null
                )}
              </FeatureFlags>
              <Animated.View
                style={{
                  height: fadeAnim.interpolate({
                    inputRange: [0, 0.5],
                    outputRange: [scale(46), 0],
                    extrapolate: 'clamp',
                  }),
                  marginTop: fadeAnim.interpolate({
                    inputRange: [0, 0.5],
                    outputRange: [scale(18), 0],
                    extrapolate: 'clamp',
                  }),
                  overflow: 'hidden',
                }}
              >
                <TouchableElement
                  onPress={() => {
                    Animated.timing(fadeAnim, {
                      toValue: 1,
                      duration: 500,
                      useNativeDriver: false,
                    }).start();
                  }}
                >
                  {({ boxStyle, pressHoverColor }) => (
                    <View
                      style={[
                        boxStyle,
                        {
                          backgroundColor: C(pressHoverColor('Button/Light/Secondary')),
                          borderRadius: scale(12),
                          flexDirection: 'row',
                          height: scale(46),
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      <View style={{ marginLeft: scale(23) }}>
                        <Text style={[font['Button 1'], { color: C('Button/Light/Secondary Text') }]}>{i18n.t('More')}</Text>
                      </View>
                      <View style={{ marginLeft: scale(6), marginTop: scale(3) }}>
                        <Svg icon="chevronBottomLight" width={scale(13)} height={scale(8)} color={'Button/Light/Secondary Text'} />
                      </View>
                    </View>
                  )}
                </TouchableElement>
              </Animated.View>
              <Animated.View
                style={{
                  height: fadeAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, scale(48 * renderButtons.length)],
                    extrapolate: 'clamp',
                  }),
                  overflow: 'hidden',
                }}
              >
                {renderButtons.map((i) =>
                  !i.isMain ? (
                    <View key={i.name}>
                      <LineButton
                        {...{
                          ...i,
                        }}
                      />
                    </View>
                  ) : null
                )}
              </Animated.View>
            </View>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default connect(
  ({ membership: { isWithERP }, globalAdd: { globalSelectedLevel, globalImageContext } }) => ({ globalSelectedLevel, globalImageContext, isWithERP }),
  {}
)(ColorWrapper(AddMenu));
